import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScheduleChatMessageModal from "../../../../components/models/ScheduleChatMessageModal";
import SweetAlert from "../../../../components/SweetAlert/SweetAlert";
import Swal from "sweetalert2";
import { decodedText } from "../../../../helpers/utils";
import { useSocket } from "../../../../context/SocketContext";
import {
    cancelScheduledChatMessage,
    fetchScheduledChatMessages,
} from "../../../../store/slices/chatSlice";
import {
    getScheduledMessagePhase,
    isActiveScheduledMessage,
} from "./scheduledMessageHelpers";
import { formatScheduledLocalDisplay } from "./scheduledTimeUtils";

const refetchActiveScheduled = (dispatch) => dispatch(fetchScheduledChatMessages({ active: true }));

const ScheduledChatMessages = () => {
    const dispatch = useDispatch();
    const { socket } = useSocket();
    const { scheduledMessages, scheduledMessagesLoading } = useSelector((state) => state.chat);
    const { user } = useSelector((state) => state.auth);
    const [editItem, setEditItem] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const roomId = user?.attributes?.stationChatId;
    const activeMessages = (scheduledMessages || []).filter(isActiveScheduledMessage);

    useEffect(() => {
        refetchActiveScheduled(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (!socket || !roomId) return;

        const onRefresh = () => refetchActiveScheduled(dispatch);
        const sendEvent = `messages.send.${roomId}`;
        const pinEvent = `messages.pin.${roomId}`;
        const unpinEvent = `messages.unpin.${roomId}`;
        const scheduledEvent = `scheduled_messages.updated.${roomId}`;

        socket.on(sendEvent, onRefresh);
        socket.on(pinEvent, onRefresh);
        socket.on(unpinEvent, onRefresh);
        socket.on(scheduledEvent, onRefresh);

        return () => {
            socket.off(sendEvent, onRefresh);
            socket.off(pinEvent, onRefresh);
            socket.off(unpinEvent, onRefresh);
            socket.off(scheduledEvent, onRefresh);
        };
    }, [socket, roomId, dispatch]);

    const handleDelete = (item) => {
        const phase = getScheduledMessagePhase(item);
        const preview = decodedText(item?.attributes?.sanitizedText || item?.attributes?.text || "");
        const isPinRemoval = phase === "pin_removal";

        SweetAlert({
            title: isPinRemoval ? "Cancel scheduled pin removal?" : "Delete scheduled message?",
            text: isPinRemoval
                ? "The message will stay pinned until you unpin it manually."
                : preview
                  ? `"${preview.slice(0, 120)}${preview.length > 120 ? "…" : ""}"`
                  : "",
            confirmAction: async () => {
                Swal.showLoading();
                await dispatch(
                    cancelScheduledChatMessage({
                        id: item.id,
                        message: isPinRemoval
                            ? "Scheduled pin removal cancelled."
                            : "Scheduled message cancelled.",
                    })
                ).unwrap();
            },
        });
    };

    const handleEdit = (item) => {
        setEditItem(item);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditItem(null);
    };


    if (activeMessages.length === 0) {
        return null;
    }

    return (
        <>
            {activeMessages.map((item) => {
                const attrs = item.attributes || {};
                const phase = getScheduledMessagePhase(item);
                const text = decodedText(attrs.sanitizedText || attrs.text || "");
                const sendLabel = formatScheduledLocalDisplay(attrs, "send");
                const removalLabel = attrs.scheduledPinRemovalAt || attrs.scheduledPinRemovalLocalDate
                    ? formatScheduledLocalDisplay(attrs, "removal")
                    : null;
                const isPinRemoval = phase === "pin_removal";

                return (
                    <div
                        key={item.id}
                        className="status-box shadow border pinned-message-container scheduled-message-row"
                    >
                        <div className="pinned-content d-flex">
                            <AccessTimeIcon className="scheduled-message-icon" aria-hidden />
                            <div className="status-text">
                                <span className="text-uniform">{text}</span>
                                {isPinRemoval ? (
                                    <>
                                        <small className="scheduled-meta">Sent & pinned</small>
                                        {removalLabel && (
                                            <small className="scheduled-meta">Unpins {removalLabel}</small>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <small className="scheduled-meta">Sends {sendLabel}</small>
                                        {attrs.pinOnSend && removalLabel && (
                                            <small className="scheduled-meta">Unpins {removalLabel}</small>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="status-label scheduled-message-actions">
                            <EditIcon
                                className="scheduled-action-icon"
                                titleAccess={
                                    isPinRemoval
                                        ? "Edit scheduled pin removal"
                                        : "Edit scheduled message"
                                }
                                onClick={() => handleEdit(item)}
                            />
                            <DeleteOutlineIcon
                                className="scheduled-action-icon scheduled-action-icon--delete"
                                titleAccess={
                                    isPinRemoval
                                        ? "Cancel scheduled pin removal"
                                        : "Delete scheduled message"
                                }
                                onClick={() => handleDelete(item)}
                            />
                        </div>
                    </div>
                );
            })}

            <ScheduleChatMessageModal
                show={showEditModal}
                onClose={closeEditModal}
                editItem={editItem}
                onScheduleSuccess={() => refetchActiveScheduled(dispatch)}
            />
        </>
    );
};

export default ScheduledChatMessages;
